import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA4 from 'react-ga4';
import { useHistory } from 'react-router-dom';

import { SearchSelect, SelectOption } from 'components/SearchSelectV2';

import { updateUserDetailQuery, useDeviceTypeIsMobile, searchUserV3 } from 'hooks';
import { OfficialAccountAvatar, UserCardPopup } from '@/components';
import { debounce, getOfficialPath, isEthAddress, isOfficalAddress } from 'utils';

import { IconRcClose, IconRcEnter, IconRcFire, IconRcHyperlink, IconRcRectPlus } from '@/assets';
import { IconHiChat } from '@/assets/hi';
import clsx from 'clsx';
import { UserCard } from '../UserCard';
import { useRecentSearch } from './hooks';
import style from './index.module.scss';
import { NotFoundContent } from '../SearchSelect/Option';

type SearchAddressProps = {
  className?: string;
  inputClassName?: string;
  inputActiveClassName?: string;
  panelClassName?: string;
  isShowRecent?: boolean;
  type?: string;
  onSelect?: (val: string) => void;
  inParent?: boolean;
  isLimitHeight?: boolean;
  includeOfficialAccount?: boolean;
  disableHi?: boolean;
  placeholder?: string;
  replaceAction?: boolean;
  iconAtEnd?: boolean;
};

const INVALID_SEARCH_LENGTH_LIMIT = 42;

export const SearchAddress = ({
  className,
  inputClassName,
  inputActiveClassName,
  panelClassName,
  isShowRecent = false,
  onSelect,
  type,
  isLimitHeight,
  inParent = true,
  includeOfficialAccount,
  disableHi,
  placeholder,
  replaceAction,
  iconAtEnd,
}: SearchAddressProps) => {
  const { t } = useTranslation();
  const recent = useRecentSearch(isShowRecent);
  const [options, setOptions] = useState<Array<{ label?: any; value?: any; children?: any }>>([]);
  const history = useHistory();
  const [isShow, setIsShow] = useState(false);
  const isMobile = useDeviceTypeIsMobile();
  const [search, setSearch] = useState('');
  const [preventBlur, setPreventBlur] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeiht] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const _options = useMemo(() => {
    if (!isShow) {
      return [];
    }
    if (!isShowRecent) {
      return options;
    }
    if (!search) {
      return recent.data;
    }
    return options;
  }, [isShowRecent, search, options, recent.data, isShow]);

  const handleSearch = useCallback(
    inputValue => {
      if (!inputValue && !isShowRecent) {
        setIsLoading(false);
        setIsShow(false);
        return;
      }
      if (inputValue && inputValue.trim().length > INVALID_SEARCH_LENGTH_LIMIT) {
        setIsLoading(false);
        setOptions([]);
        return;
      }
      setIsLoading(true);
      searchUserV3(inputValue)
        .then(res => {
          setIsLoading(false);
          setOptions(
            (res?.users || [])
              .filter(x => (includeOfficialAccount ? x : x?.id.toString().length !== 6))
              .map((item: any) => {
                return {
                  value: item,
                  _type: 'default',
                  className: isEthAddress(inputValue) ? style.optionAddress : null,
                };
              }),
          );
          setIsShow(true);
        })
        .catch(() => {
          setIsLoading(false);
        });
    },
    [isShowRecent, includeOfficialAccount],
  );

  const onMemoChange = useCallback(
    (desc: UserDesc, memo: string) => {
      if (search) {
        handleSearch(search);
      } else {
        recent.refetch();
      }
      try {
        updateUserDetailQuery({
          id: desc.id,
          desc: {
            ...desc,
            user: {
              ...desc.user,
              memo,
            },
          },
          comment: memo,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [handleSearch, recent, search],
  );

  const debouceSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

  const hideSearch = useCallback(() => {
    setSearch('');
    setIsShow(false);
    setPreventBlur(false);
    setOptions([]);
  }, []);

  const navPage = useCallback(
    ({ value, _type }: SelectOption) => {
      if (isShowRecent && _type !== 'hot') {
        recent.add(value.id);
      }
      hideSearch();

      if (onSelect) {
        onSelect(value.id);
      } else {
        ReactGA4.event({
          category: 'Search',
          action: `Profiles`,
          label: `${value.id}`,
        });
        const url = isOfficalAddress(value.id)
          ? `${getOfficialPath(value as OfficialAccount)}`
          : `/profile/${value.id}`;

        if (replaceAction) {
          history.replace(url);
          return;
        }

        history.push(url);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, isShowRecent, onSelect, recent.add, replaceAction],
  );

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const input = searchRef.current;
      const dropdown = document.querySelector('.db-search-address-panel');
      const memo = document.querySelector('.memo-edit-modal');

      if ([input, dropdown, memo].some(el => el?.contains(event.target))) {
        setPreventBlur(true);
        return;
      }
      hideSearch();
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [hideSearch]);

  useEffect(() => {
    if (isShowRecent) {
      if (!isShow) {
        recent.setIsShowMore(false);
      } else {
        recent.refetch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, recent.refetch, recent.setIsShowMore, isShowRecent]);

  useEffect(() => {
    if (isLimitHeight) {
      const viewPortHeight = window.innerHeight;
      const rect = searchRef?.current?.getBoundingClientRect();
      if (rect) {
        const height = viewPortHeight - rect.bottom - 10;
        setMaxHeiht(height > 0 ? height : 0);
      }
    } else {
      setMaxHeiht(0);
    }
  }, [isLimitHeight]);

  const renderOption = useCallback(
    (option: any) => (
      <SearchUserOption
        onHide={hideSearch}
        onMemoChange={v => onMemoChange?.(option.value.desc, v)}
        type={type || option?._type}
        data={isOfficalAddress(option?.value?.id) ? option.value : option?.value?.desc}
        search={search}
        isActive={option._isActive}
        onRemoveRecent={recent.remove}
        disableHi={disableHi}
      />
    ),
    [hideSearch, onMemoChange, recent.remove, search, type, disableHi],
  );

  const onSearch = useCallback(
    v => {
      if (v) {
        setIsLoading(true);
      }
      debouceSearch(v);
    },
    [debouceSearch],
  );

  const onFocus = useCallback(() => {
    setIsShow(true);
  }, []);

  const panelStyle = useMemo(
    () => (isLimitHeight && maxHeight ? { maxHeight } : {}),
    [isLimitHeight, maxHeight],
  );

  const notFoundContent = useMemo(
    () =>
      isLoading || !search ? null : (
        <NotFoundContent
          placeholder={
            search?.trim()?.length > INVALID_SEARCH_LENGTH_LIMIT
              ? 'Search text is too long'
              : undefined
          }
        />
      ),
    [isLoading, search],
  );

  return (
    <SearchSelect
      ref={searchRef}
      preventBlur={preventBlur}
      setPreventBlur={setPreventBlur}
      inParent={inParent}
      prefix={type ? false : undefined}
      iconAtEnd={iconAtEnd}
      placeholder={placeholder || t('SearchAddress.placeholder')}
      options={_options as any}
      open={isShow}
      onSearch={onSearch}
      onSelect={navPage}
      onBlur={hideSearch}
      onFocus={onFocus}
      setOptions={setOptions}
      className={cx(
        style.headSearch,
        isMobile && style.mobileSearch,
        'db-search-address',
        className,
      )}
      inputClassName={cx(
        style.headInputSearch,
        isMobile && style.mobileInputSearch,
        inputClassName,
      )}
      inputActiveClassName={inputActiveClassName}
      panelStyle={panelStyle}
      panelClassName={cx(
        style.resultPanel,
        isMobile && style.mobileSearchDropdown,
        panelClassName,
        'db-search-address-panel',
      )}
      searchValue={search}
      onSearchValueChange={setSearch}
      notFoundContent={notFoundContent}
      renderOption={renderOption}
    />
  );
};

export default SearchAddress;

const SearchUserOption = ({
  type,
  search,
  data,
  onRemoveRecent,
  isActive,
  onMemoChange,
  onHide,
  disableHi,
}: {
  data?: any;
  type?: 'recent' | 'hot' | 'default' | 'bundle' | 'hi';
  search?: string;
  onRemoveRecent?: (id: string) => void;
  isActive?: boolean;
  onMemoChange?: (value: string) => void;
  onHide?: () => void;
  disableHi?: boolean;
}) => {
  const { t } = useTranslation();

  const history = useHistory();
  const isAddress =
    isEthAddress(search?.trim()) && search?.toLowerCase()?.trim() === data?.id?.toLowerCase();
  const isOfficialAccount = useMemo(() => isOfficalAddress(data?.id), [data]);

  return (
    <div className={style.optionContainer}>
      <div className={clsx(style.option)}>
        {isOfficialAccount ? (
          <div className={style.officialAccount}>
            <OfficialAccountAvatar
              logoVisible
              size={28}
              className={style.officialAccountAvatar}
              userCardPopup
              data={data}
              logoOption={{
                size: 18,
                customStyle: {
                  right: '-3px',
                  bottom: '-3px',
                },
              }}
            />
            <div>
              <div className={style.officialId}>
                <UserCardPopup address={String(data?.id)}>{data.name}</UserCardPopup>
              </div>

              <div className={style.officialAccountTag}>{t('SearchAddress.ynzhfc')}</div>
            </div>
          </div>
        ) : (
          <UserCard
            isShowAmount
            extra={type === 'hot' ? <IconRcFire className={style.hotIcon} /> : null}
            data={data}
            onMemoChange={onMemoChange}></UserCard>
        )}
        <div className={style.extra}>
          {type === 'recent' ? (
            <IconRcClose
              className={style.close}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveRecent?.(data?.id);
              }}
            />
          ) : null}
          {!disableHi && ['recent', 'hi', 'default'].includes(type || '') ? (
            <div
              className={style.hi}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onHide?.();
                history.replace(`/hi/${isOfficialAccount ? `0a${data?.id}` : data?.id}`);
              }}>
              <img src={IconHiChat} alt="" />
              Hi
            </div>
          ) : null}
          {type === 'bundle' ? (
            <div className={style.plus}>
              <IconRcRectPlus></IconRcRectPlus>
              {t('SearchAddress.1eo1')}
            </div>
          ) : null}
        </div>
        {isActive && type !== 'hot' ? <IconRcEnter className={style.enter} /> : null}
      </div>
      {isAddress ? (
        <div className={style.address}>
          <IconRcHyperlink className={style.hyperlink}></IconRcHyperlink>
          {data?.id}
        </div>
      ) : null}
    </div>
  );
};
